import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, Drawer, Typography } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClipboardList } from '@fortawesome/free-solid-svg-icons';
import { renderBody } from '../Utils/utils';
import chatBackground from '../../../../assets/image/chat-bg.png';
import MetaFlowDrawerContent from './MetaFlowDrawerContent';

const { Text } = Typography;

const PREVIEW_FIXED_BODY =
  'Tap the button below to open this flow and complete the steps.';
const PREVIEW_FIXED_FOOTER = 'Managed by the business.';
const PREVIEW_FIXED_BUTTON = 'View flow';

const styles = {
  chatBackground: {
    backgroundImage: `url(${chatBackground})`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    borderRadius: '8px',
    padding: '16px',
    minHeight: 560,
  },
  chatBubble: {
    background: 'white',
    borderRadius: '8px',
    padding: '16px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
    position: 'relative',
    marginLeft: '20px',
    maxWidth: '85%',
    wordWrap: 'break-word',
  },
  buttonBubble: {
    background: 'white',
    borderRadius: '8px',
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    boxShadow: '0 8px 8px -4px rgba(0, 0, 0, 0.15)',
    position: 'relative',
    bottom: '0.5rem',
    marginLeft: '20px',
    maxWidth: '85%',
    wordWrap: 'break-word',
  },
  chatBubbleBefore: {
    content: '""',
    position: 'absolute',
    top: '0',
    left: '-5px',
    width: '0',
    height: '0',
    borderStyle: 'solid',
    borderWidth: '10px 10px 10px 0',
    borderColor: 'transparent white transparent transparent',
    background: '#ffff',
    backgroundColor: '#ffff',
    clipPath: 'polygon(0 0, 0 100%, 100% 100%)',
    transform: 'rotate(180deg)',
  },
  chatTime: {
    textAlign: 'right',
    fontSize: '12px',
    color: 'gray',
    marginTop: '8px',
  },
  footerMessageStyle: {
    fontSize: '12px',
    marginTop: '5px',
    color: '#918f8f',
  },
  flowButton: {
    wordWrap: 'break-word',
    fontFamily: 'system-ui',
    display: 'block',
    textAlign: 'center',
    color: '#00a5f4',
    fontWeight: 600,
  },
};

const MetaFlowPreview = ({
  flowName,
  screens = [],
  activeScreenId,
  onScreenChange,
}) => {
  const containerRef = useRef(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState('');

  useEffect(() => {
    const updateCurrentTime = () => {
      const now = new Date();
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      setCurrentTime(`${hours}:${minutes}`);
    };

    updateCurrentTime();
    const interval = setInterval(updateCurrentTime, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!drawerOpen) return;

    const sharedStyles = {
      borderBottomLeftRadius: '1rem',
      borderBottomRightRadius: '1rem',
      boxShadow: 'none',
      overflow: 'hidden',
    };

    const container = containerRef.current;
    if (!container) return;

    const elements = container.querySelectorAll('.ant-drawer-content, .ant-drawer-content-wrapper');
    elements.forEach((element) => {
      Object.assign(element.style, sharedStyles);
    });
  }, [drawerOpen]);

  const activeScreen = useMemo(() => {
    if (!screens.length) return null;
    if (activeScreenId) {
      return screens.find((screen) => screen.id === activeScreenId) || screens[0];
    }
    return screens[0];
  }, [activeScreenId, screens]);

  const handleOpenFlow = () => {
    if (!screens.length) return;
    if (!activeScreenId && screens[0]?.id) {
      onScreenChange?.(screens[0].id);
    }
    setDrawerOpen(true);
  };

  const handleFlowNext = () => {
    if (!activeScreen || !screens.length) {
      setDrawerOpen(false);
      return;
    }

    const screenIndex = screens.findIndex((s) => s.id === activeScreen.id);
    if (screenIndex < screens.length - 1) {
      const nextId = screens[screenIndex + 1].id;
      onScreenChange?.(nextId);
      return;
    }

    setDrawerOpen(false);
  };

  const handleFlowBack = () => {
    if (!activeScreen || !screens.length) return;
    const screenIndex = screens.findIndex((s) => s.id === activeScreen.id);
    if (screenIndex > 0) {
      onScreenChange?.(screens[screenIndex - 1].id);
    }
  };

  const bubbleHeader = flowName || 'MetaFlow';

  return (
    <Card
      title="Flow preview"
      bordered={false}
      style={{ position: 'sticky', top: 16 }}
    >
      <div ref={containerRef} style={{ position: 'relative', overflow: 'hidden' }}>
        <div style={styles.chatBackground}>
          <div style={styles.chatBubble}>
            <div style={styles.chatBubbleBefore} />
            <div className="header">
              <h5 style={{ marginBottom: 8 }}>{bubbleHeader}</h5>
            </div>
            <div className="body">{renderBody(PREVIEW_FIXED_BODY)}</div>
            <span style={styles.footerMessageStyle}>{PREVIEW_FIXED_FOOTER}</span>
            <div style={styles.chatTime}>{currentTime}</div>
          </div>

          <div style={styles.buttonBubble}>
            <span className="border-top py-2" style={{ display: 'block' }}>
              <button
                type="button"
                className="border-0 bg-transparent w-100 fw-bold d-flex align-items-center justify-content-center gap-2"
                style={{ color: '#00a5f4', cursor: screens.length ? 'pointer' : 'not-allowed' }}
                onClick={handleOpenFlow}
                disabled={!screens.length}
              >
                <FontAwesomeIcon icon={faClipboardList} />
                {PREVIEW_FIXED_BUTTON}
              </button>
            </span>
          </div>
        </div>

        <Drawer
          title={null}
          placement="bottom"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          height="90%"
          closable={false}
          mask={false}
          getContainer={false}
          rootStyle={{ position: 'absolute' }}
          bodyStyle={{ padding: 0, height: '100%' }}
          destroyOnClose={false}
        >
          <MetaFlowDrawerContent
            screen={activeScreen}
            screens={screens}
            onClose={() => setDrawerOpen(false)}
            onNext={handleFlowNext}
            onBack={handleFlowBack}
          />
        </Drawer>
      </div>

      <Text type="secondary" style={{ display: 'block', marginTop: 12, fontSize: 12 }}>
        Tap &quot;{PREVIEW_FIXED_BUTTON}&quot; to preview your flow inside the chat.
      </Text>
    </Card>
  );
};

export default MetaFlowPreview;
